import { invalidateMinicart } from "~/app/store/bffApi";
import { occBaseSiteUrl, occFetch } from "~/app/api/occ/occFetch";
import { sessionQueryOptions } from "~/app/api/pageData";
import { queryClient } from "~/app/queryClient";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { getCurrentBrowserTimezone } from "~/common/utils/dateUtils";
import type { MoxyDateString } from "~/common/types/MoxyDate";
import type { OccRequestOptions } from "~/common/api/occTransport";
import { ensureAuthenticatedActiveCartForSession } from "~/features/cart/cart/api/CartEnsure";

export interface AddToQuoteApiItem {
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;
  comment?: string;
  internalComment?: string;
  selectedDeliveryDate: MoxyDateString;
  primaryWarehouseCode: string;
  offerPrice?: number;
  targetPrice?: number;
  minimumSellingPrice?: number;
  recommendedSellingPrice?: number;
  priceReference?: string;
}

export async function sendAddToQuote(
  data: AddToQuoteApiItem | AddToQuoteApiItem[],
  _context?: { recommendationType?: PurchaseType; recommendationPlace?: PurchasePlace },
) {
  const session = await queryClient.fetchQuery({ ...sessionQueryOptions(), staleTime: Infinity });
  const selectedAccount = session.commerceContext.selectedAccount;
  if (!selectedAccount?.uid || !selectedAccount.salesOrg) {
    throw new Error("Cannot add items to a Quote Cart without an explicit account context");
  }
  const activeQuoteCartCode =
    session.activeCart?.code ?? (await ensureAuthenticatedActiveCartForSession(session)).identity.code;

  const arrayData = Array.isArray(data) ? data : [data];
  const body = {
    items: arrayData,
    userTimeZone: getCurrentBrowserTimezone(),
  };
  const accountContext: OccRequestOptions["accountContext"] = {
    soldTo: selectedAccount.uid,
    salesOrg: selectedAccount.salesOrg,
    endCustomer: selectedAccount.endCustomerCode,
  };

  const result = await occFetch(
    occBaseSiteUrl(`/users/current/carts/${encodeURIComponent(activeQuoteCartCode)}/quote/add/bulk`),
    {
      body: JSON.stringify(body),
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { accountContext },
  );
  invalidateMinicart();
  if (!result.ok) {
    throw new Error("Failed to add items to quote");
  }
  return result;
}
